import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Briefcase, ListChecks, Table2, LogOut,
  SlidersHorizontal, FileCheck2, ReceiptText, Activity, UserCog, Settings, PanelLeftClose, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const mainNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/clienti", label: "Clienti", icon: Users, testid: "nav-clienti", tree: "clienti", adminOnly: true },
  { to: "/commesse", label: "Commesse", icon: Briefcase, testid: "nav-commesse", tree: "commesse" },
  { to: "/listino", label: "Listini", icon: ListChecks, testid: "nav-listino", tree: "listini", adminOnly: true },
  { to: "/contabilita", label: "Contabilità", icon: Table2, testid: "nav-contabilita" },
  { to: "/fatture", label: "Fatture", icon: ReceiptText, testid: "nav-fatture" },
  { to: "/anagrafiche", label: "Anagrafiche", icon: SlidersHorizontal, testid: "nav-anagrafiche" },
];

const adminNav = [
  { to: "/attivita", label: "Attività", icon: Activity, testid: "nav-attivita", adminOnly: true },
  { to: "/utenti", label: "Utenti", icon: UserCog, testid: "nav-utenti", adminOnly: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, testid: "nav-impostazioni", adminOnly: true },
];

const SIDEBAR_KEY = "cc_sidebar_collapsed";
const SIDEBAR_WIDTH_KEY = "cc_sidebar_width";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === "1");
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const v = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY) || "256", 10);
    return Number.isFinite(v) ? Math.min(480, Math.max(200, v)) : 256;
  });
  const [expanded, setExpanded] = useState({});
  const [trees, setTrees] = useState({ clienti: [], commesse: [], listini: [] });
  const [expandedCom, setExpandedCom] = useState({});

  const startResize = (e) => {
    e.preventDefault();
    const onMove = (ev) => setSidebarWidth(Math.min(480, Math.max(200, ev.clientX)));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
      setSidebarWidth((w) => { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w)); return w; });
    };
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const loadTrees = useCallback(() => {
    if (!user) return;
    Promise.all([api.get("/clienti"), api.get("/commesse"), api.get("/listini")])
      .then(([c, co, l]) => setTrees({ clienti: c.data, commesse: co.data, listini: l.data }))
      .catch(() => {});
  }, [user]);

  useEffect(() => { loadTrees(); }, [loadTrees, location.pathname]);

  const treeItem = {
    clienti: (x) => ({ id: x.id, label: x.nome, onClick: () => navigate(`/commesse?cliente=${x.id}`) }),
    commesse: (x) => ({ id: x.id, label: `${x.codice} · ${x.nome}`, onClick: () => navigate(`/contabilita?commessa=${x.id}`) }),
    listini: (x) => ({ id: x.id, label: `${x.nome} · ${x.tipo}`, onClick: () => navigate(`/listino?listino=${x.id}`) }),
  };

  const toggle = () => setCollapsed((c) => {
    const next = !c;
    localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
    return next;
  });

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const rowClass = (active) =>
    cn(
      "flex items-center gap-3 py-2.5 text-sm border-l-4 transition-colors w-full",
      collapsed ? "justify-center px-0" : "pl-4 pr-3",
      active
        ? "border-blue-700 bg-blue-50 text-blue-800 font-semibold"
        : "border-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 font-medium",
    );

  return (
    <div className="flex h-screen w-full bg-slate-50 overflow-hidden">
      <div className="grain-overlay" aria-hidden="true" />
      <aside style={collapsed ? undefined : { width: sidebarWidth }} className={cn("relative flex-shrink-0 border-r border-slate-200 bg-white flex flex-col z-10", collapsed ? "w-16 transition-all duration-200" : "")}>
        <div className={cn("h-16 flex items-center border-b border-slate-200", collapsed ? "justify-center px-0" : "justify-between px-5")}>
          {!collapsed ? (
            <div className="flex items-center gap-2" data-testid="sidebar-logo">
              <img src="/powercom-accounting-emblem-white.png" alt="Emblema contabilità" className="h-8 w-8 flex-shrink-0" />
              <img src="/powercom-logo.webp" alt="POWERCOM" className="h-5 w-auto" />
            </div>
          ) : (
            <button onClick={toggle} data-testid="sidebar-logo-collapsed" aria-label="Espandi menu" title="Espandi menu">
              <img src="/powercom-accounting-emblem-white.png" alt="POWERCOM" className="h-8 w-8" />
            </button>
          )}
          {!collapsed && (
          <button
            data-testid="sidebar-toggle" onClick={toggle}
            className="h-8 w-8 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Comprimi menu" title="Comprimi menu"
          >
            <PanelLeftClose className="h-5 w-5" />
          </button>
          )}
        </div>

        <nav className="flex-1 py-3 overflow-y-auto overflow-x-hidden">
          {mainNav.map((item) => {
            if (item.adminOnly && user?.role !== "admin") return null;
            const active = item.end ? location.pathname === item.to : location.pathname.startsWith(item.to) && item.to !== "/";
            if (item.tree && !collapsed) {
              const isOpen = !!expanded[item.tree];
              const items = trees[item.tree] || [];
              return (
                <div key={item.to}>
                  <button
                    data-testid={item.testid} onClick={() => { navigate(item.to); setExpanded((e) => ({ ...e, [item.tree]: !e[item.tree] })); }}
                    className={rowClass(active)}
                  >
                    <item.icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} />
                    <span className="flex-1 text-left">{item.label}</span>
                    <ChevronRight className={cn("h-4 w-4 text-slate-400 transition-transform", isOpen && "rotate-90")} />
                  </button>
                  {isOpen && (
                    <div className="py-1 pr-2 pl-11 max-h-72 overflow-y-auto space-y-0.5 bg-slate-50/60">
                      {items.length === 0 && <div className="text-xs text-slate-300 py-1">Nessun elemento</div>}
                      {item.tree === "commesse"
                        ? items.filter((p) => !p.parent_id).map((p) => {
                            const kids = items.filter((c) => c.parent_id === p.id);
                            const openP = !!expandedCom[p.id];
                            return (
                              <div key={p.id}>
                                <div className="flex items-center gap-1">
                                  {kids.length > 0 ? (
                                    <button data-testid={`tree-com-toggle-${p.id}`} onClick={() => setExpandedCom((e) => ({ ...e, [p.id]: !e[p.id] }))}
                                      className="text-slate-400 hover:text-blue-700 flex-shrink-0" title={openP ? "Comprimi" : "Espandi"}>
                                      <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", openP && "rotate-90")} />
                                    </button>
                                  ) : <span className="w-3.5 flex-shrink-0" />}
                                  <button data-testid={`tree-commesse-${p.id}`} onClick={() => navigate(kids.length ? `/commesse/${p.id}` : `/contabilita?commessa=${p.id}`)}
                                    className="flex-1 text-left text-xs text-slate-500 hover:text-blue-700 truncate py-1" title={`${p.codice} · ${p.nome}`}>
                                    {p.codice} · {p.nome}
                                  </button>
                                </div>
                                {openP && kids.map((k) => (
                                  <button key={k.id} data-testid={`tree-commesse-${k.id}`} onClick={() => navigate(`/commesse/${k.id}`)}
                                    className="block w-full text-left text-xs text-slate-500 hover:text-blue-700 truncate py-1 pl-6" title={`${p.nome} / ${k.codice} · ${k.nome}`}>
                                    <span className="text-slate-300">↳</span> {k.codice} · {k.nome}
                                  </button>
                                ))}
                              </div>
                            );
                          })
                        : items.map((x) => {
                            const it = treeItem[item.tree](x);
                            return (
                              <button key={it.id} data-testid={`tree-${item.tree}-${it.id}`} onClick={it.onClick}
                                className="block w-full text-left text-xs text-slate-500 hover:text-blue-700 truncate py-1" title={it.label}>
                                {it.label}
                              </button>
                            );
                          })}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
                className={({ isActive }) => rowClass(isActive)} title={collapsed ? item.label : undefined}>
                <item.icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} />
                {!collapsed && item.label}
              </NavLink>
            );
          })}

          {user?.role === "admin" && (
            <>
              {!collapsed && (
                <div className="px-4 pt-5 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Amministrazione</div>
              )}
              {collapsed && <div className="my-3 mx-3 border-t border-slate-100" />}
              {adminNav.map((item) => (
                <NavLink key={item.to} to={item.to} data-testid={item.testid}
                  className={({ isActive }) => rowClass(isActive)} title={collapsed ? item.label : undefined}>
                  <item.icon className="h-[18px] w-[18px] flex-shrink-0" strokeWidth={2} />
                  {!collapsed && item.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className={cn("flex items-center gap-3 py-2 mb-1", collapsed ? "justify-center px-0" : "px-2")}>
            <div className="h-8 w-8 flex-shrink-0 rounded-md bg-slate-900 text-white flex items-center justify-center text-xs font-bold uppercase">
              {(user?.name || user?.email || "U").slice(0, 1)}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-800 truncate">{user?.name || user?.email}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate">{user?.role === "admin" ? "Admin" : "Utente"}</div>
              </div>
            )}
          </div>
          <Button variant="ghost" data-testid="logout-button" onClick={handleLogout}
            className={cn("w-full text-slate-500 hover:text-rose-600 hover:bg-rose-50 h-9", collapsed ? "justify-center px-0" : "justify-start")}
            title={collapsed ? "Esci" : undefined}>
            <LogOut className="h-4 w-4" /> {!collapsed && <span className="ml-2">Esci</span>}
          </Button>
        </div>
        {!collapsed && (
          <div data-testid="sidebar-resize-handle" onMouseDown={startResize}
            className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/50 active:bg-blue-500/60 z-20" />
        )}

      </aside>

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden z-10">
        <Outlet />
      </main>
    </div>
  );
}
